/**
 * Customer Portal (UI) - application chrome and top-level layout.
 */
import { useState, type FormEvent } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { authClient } from '../../features/auth/client';
import type { ViewerSession } from '../../types/portal';
import { getTimezoneContextLabel, type TimeDisplayMode } from '../../utils/datetime';

type PortalLayoutProps = {
  session: ViewerSession | null;
  timeDisplayMode: TimeDisplayMode;
  onChangeTimeDisplayMode: (mode: TimeDisplayMode) => void;
};

function classForNavLink({ isActive }: { isActive: boolean }): string {
  return isActive ? 'nav-link active' : 'nav-link';
}

export const PORTAL_NAV_ITEMS = [
  { to: '/', label: 'Home', end: true },
  { to: '/messages', label: 'Messages' },
  { to: '/keywords', label: 'Keywords' },
  { to: '/opt-outs', label: 'Opt-outs' },
  { to: '/navigator', label: 'Navigator' },
  { to: '/brands', label: 'Brands' },
  { to: '/campaigns', label: 'Campaigns' },
  { to: '/templates', label: 'Templates' },
  { to: '/billing', label: 'Billing' },
  { to: '/reports', label: 'Reports' },
  { to: '/settings', label: 'Settings' },
  { to: '/support', label: 'Support' }
] as const;

function formatAccountTypeLabel(type: ViewerSession['accountType'] | ViewerSession['orgType'] | null | undefined): string {
  if (!type) return '';
  return type
    .split('_')
    .map((part) => (part.length > 0 ? `${part[0].toUpperCase()}${part.slice(1)}` : part))
    .join(' ');
}

export function PortalLayout({ session, timeDisplayMode, onChangeTimeDisplayMode }: PortalLayoutProps) {
  const timezoneLabel = getTimezoneContextLabel(timeDisplayMode);
  const [signingOut, setSigningOut] = useState(false);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordSubmitting, setPasswordSubmitting] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState<string | null>(null);
  const userName = session?.userName?.trim() ?? '';
  const userEmail = session?.userEmail?.trim() ?? '';
  const primaryIdentityLabel = userName || userEmail || 'Signed in';
  const secondaryIdentityLabel = userName && userEmail ? userEmail : '';
  const effectiveAccountType = session?.accountType ?? session?.orgType ?? null;
  const accountTypeLabel = formatAccountTypeLabel(effectiveAccountType);

  function clearPasswordForm(): void {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError(null);
    setPasswordSuccess(null);
  }

  function openProfileModal(): void {
    clearPasswordForm();
    setProfileModalOpen(true);
  }

  function closeProfileModal(): void {
    if (passwordSubmitting) return;
    setProfileModalOpen(false);
  }

  async function submitPasswordChange(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setPasswordError(null);
    setPasswordSuccess(null);
    const current = currentPassword;
    const next = newPassword;
    const confirm = confirmPassword;
    if (!current || !next || !confirm) {
      setPasswordError('Current password, new password, and confirmation are required.');
      return;
    }
    if (next !== confirm) {
      setPasswordError('New password and confirmation must match.');
      return;
    }
    if (next.length < 8) {
      setPasswordError('New password must be at least 8 characters.');
      return;
    }

    setPasswordSubmitting(true);
    try {
      const changePassword = (authClient as any).changePassword;
      if (typeof changePassword === 'function') {
        const result = await changePassword({
          currentPassword: current,
          newPassword: next,
          revokeOtherSessions: false
        });
        if (result?.error) {
          throw new Error(result.error.message ?? 'Unable to update password right now.');
        }
      } else {
        const response = await fetch('/api/auth/change-password', {
          method: 'POST',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({
            currentPassword: current,
            newPassword: next,
            revokeOtherSessions: false
          })
        });
        if (!response.ok) {
          const payload = await response.json().catch(() => null);
          const message =
            typeof payload?.message === 'string'
              ? payload.message
              : typeof payload?.error?.message === 'string'
                ? payload.error.message
                : `Unable to update password (${response.status}).`;
          throw new Error(message);
        }
      }

      setPasswordSuccess('Password updated successfully.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (requestError) {
      setPasswordError(requestError instanceof Error ? requestError.message : String(requestError));
    } finally {
      setPasswordSubmitting(false);
    }
  }

  async function handleLogout(): Promise<void> {
    setSigningOut(true);
    try {
      await (authClient.signOut as any)();
    } catch (_error) {
      // Ignore sign-out network failures and continue with a hard redirect.
    } finally {
      window.location.assign('/login');
    }
  }

  return (
    <div className="page">
      <header className="navbar navbar-expand-md navbar-light border-bottom">
        <div className="container-xl">
          <a
            className="navbar-brand pe-0 pe-md-3 d-flex align-items-center"
            href="/"
            aria-label="Pronto Connects home"
          >
            <img
              className="pronto-brand-logo"
              src="https://prontoconnects.com/wp-content/uploads/2025/07/Pronto-Connects-Logo_Black-Web-01.png"
              alt="Pronto Connects"
            />
          </a>
          <div className="navbar-nav flex-row ms-auto align-items-center gap-2">
            <div className="d-flex flex-column align-items-end pe-2">
              <div className="btn-group btn-group-sm" role="group" aria-label="Timezone display mode">
                <button
                  className={timeDisplayMode === 'local' ? 'btn btn-primary' : 'btn btn-outline-secondary'}
                  type="button"
                  onClick={() => onChangeTimeDisplayMode('local')}
                  aria-pressed={timeDisplayMode === 'local'}
                >
                  Local
                </button>
                <button
                  className={timeDisplayMode === 'utc' ? 'btn btn-primary' : 'btn btn-outline-secondary'}
                  type="button"
                  onClick={() => onChangeTimeDisplayMode('utc')}
                  aria-pressed={timeDisplayMode === 'utc'}
                >
                  UTC
                </button>
              </div>
              <div className="text-secondary small lh-1 mt-1">Times: {timezoneLabel}</div>
            </div>
            <div className="d-flex align-items-center gap-2 ps-1">
              <button
                className="profile-identity-button text-start"
                type="button"
                aria-haspopup="dialog"
                aria-expanded={profileModalOpen}
                onClick={openProfileModal}
              >
                <div className="fw-semibold lh-1">{primaryIdentityLabel}</div>
                {secondaryIdentityLabel ? <div className="text-secondary small lh-1 mt-1">{secondaryIdentityLabel}</div> : null}
                {accountTypeLabel ? (
                  <div className="text-secondary small lh-1 mt-1" aria-label={`Account type: ${accountTypeLabel}`}>
                    Account type: {accountTypeLabel}
                  </div>
                ) : null}
              </button>
              <button className="btn btn-outline-secondary btn-sm" type="button" onClick={handleLogout} disabled={signingOut}>
                {signingOut ? 'Logging out...' : 'Logout'}
              </button>
            </div>
          </div>
        </div>
      </header>

      <header className="navbar navbar-expand-md border-bottom">
        <div className="container-xl">
          <ul className="navbar-nav">
            {PORTAL_NAV_ITEMS.map((item) => (
              <li key={item.to} className="nav-item">
                <NavLink to={item.to} end={'end' in item ? item.end : false} className={classForNavLink}>
                  <span className="nav-icon-dot" />
                  {item.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </div>
      </header>

      <div className="page-wrapper">
        <div className="page-body">
          <div className="container-xl">
            <Outlet />
          </div>
        </div>
      </div>

      {profileModalOpen ? (
        <div className="modal modal-blur fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.3)' }} role="dialog" aria-modal="true">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <form onSubmit={submitPasswordChange}>
                <div className="modal-header">
                  <h5 className="modal-title">Change password</h5>
                  <button type="button" className="btn-close" aria-label="Close" onClick={closeProfileModal} disabled={passwordSubmitting} />
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Current password</label>
                    <input
                      type="password"
                      className="form-control"
                      value={currentPassword}
                      onChange={(event) => setCurrentPassword(event.target.value)}
                      autoComplete="current-password"
                      disabled={passwordSubmitting}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">New password</label>
                    <input
                      type="password"
                      className="form-control"
                      value={newPassword}
                      onChange={(event) => setNewPassword(event.target.value)}
                      autoComplete="new-password"
                      disabled={passwordSubmitting}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Confirm new password</label>
                    <input
                      type="password"
                      className="form-control"
                      value={confirmPassword}
                      onChange={(event) => setConfirmPassword(event.target.value)}
                      autoComplete="new-password"
                      disabled={passwordSubmitting}
                      required
                    />
                  </div>
                  {passwordError ? <div className="alert alert-danger mb-0">{passwordError}</div> : null}
                  {passwordSuccess ? <div className="alert alert-success mb-0">{passwordSuccess}</div> : null}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-link link-secondary" onClick={closeProfileModal} disabled={passwordSubmitting}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={passwordSubmitting}>
                    {passwordSubmitting ? 'Saving...' : 'Update password'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
